
import React, { useEffect } from 'react';
import { GenericScheduleProps, Schedule22OtherSubScheduleData, OtherScheduleIncomeLossItem, OtherScheduleUnrealisedGainLossItem, OtherScheduleDeferredGainLossItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { RadioGroup } from '../ui/RadioGroup';
import { safeParseFloat } from '../../utils/calculations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-6 mb-3 border-b pb-1">{title}</h4>
);

// Sub-component for 22.1: Income/Losses not subsequently reported in Income Statement
const IncomeLossesNotInISForm: React.FC<{
  items: OtherScheduleIncomeLossItem[];
  onItemChange: (index: number, field: keyof OtherScheduleIncomeLossItem, value: any) => void;
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  errors: FormErrorsStep | undefined;
  t: GenericScheduleProps<Schedule22OtherSubScheduleData>['t'];
  realisationBasisElected: boolean;
}> = ({ items, onItemChange, onAddItem, onRemoveItem, errors, t, realisationBasisElected }) => {
  return (
    <div>
      {items.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-2 bg-slate-50 relative mb-3">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-600">{t('sch22_item_heading', { index: index + 1 })}</p>
            <Button onClick={() => onRemoveItem(item.id)} variant="danger_outline" size="sm">{t('remove_item_button')}</Button>
          </div>
          <Input label={t('sch22_description_asset_liability')} type="text" id={`s22_1_desc-${item.id}`} value={item.description} onChange={e => onItemChange(index, 'description', e.target.value)} error={(errors as any)?.[index]?.description as string} />
          <RadioGroup label={t('sch22_1_is_unrealised_label')} name={`s22_1_isUnrealised-${item.id}`} value={item.isUnrealised} onChange={val => onItemChange(index, 'isUnrealised', val)} options={[{label:t('yes'), value:'yes'}, {label:t('no'), value:'no'}]} t={t} error={(errors as any)?.[index]?.isUnrealised as string} />
          {item.isUnrealised === 'yes' && realisationBasisElected && (
            <p className="text-xs text-orange-600 bg-orange-50 p-2 rounded">{t('sch22_1_unrealised_realisation_note')}</p>
          )}
          <Input label={t('sch22_1_original_cost_label')} type="number" id={`s22_1_origCost-${item.id}`} value={item.originalCost || ''} onChange={e => onItemChange(index, 'originalCost', e.target.value)} error={(errors as any)?.[index]?.originalCost as string} />
          <Input label={t('sch22_1_nbv_eop_label')} type="number" id={`s22_1_nbv-${item.id}`} value={item.netBookValueEndOfPeriod || ''} onChange={e => onItemChange(index, 'netBookValueEndOfPeriod', e.target.value)} error={(errors as any)?.[index]?.netBookValueEndOfPeriod as string}/>
          <Input label={t('sch22_1_income_recognised_fs_not_is_label')} type="number" id={`s22_1_incRec-${item.id}`} value={item.incomeRecognisedNotInIS || ''} onChange={e => onItemChange(index, 'incomeRecognisedNotInIS', e.target.value)} error={(errors as any)?.[index]?.incomeRecognisedNotInIS as string} />
          <Input label={t('sch22_1_loss_recognised_fs_not_is_label')} type="number" id={`s22_1_lossRec-${item.id}`} value={item.lossRecognisedNotInIS || ''} onChange={e => onItemChange(index, 'lossRecognisedNotInIS', e.target.value)} error={(errors as any)?.[index]?.lossRecognisedNotInIS as string}/>
        </div>
      ))}
      <Button onClick={onAddItem} variant="secondary" size="sm">{t('sch22_add_item_button')}</Button>
    </div>
  );
};

// Sub-component for 22.2: Unrealised Gains/Losses (if Realisation Basis elected)
const UnrealisedGainsLossesForm: React.FC<{
  items: OtherScheduleUnrealisedGainLossItem[];
  onItemChange: (index: number, field: keyof OtherScheduleUnrealisedGainLossItem, value: string) => void;
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  errors: FormErrorsStep | undefined;
  t: GenericScheduleProps<Schedule22OtherSubScheduleData>['t'];
}> = ({ items, onItemChange, onAddItem, onRemoveItem, errors, t }) => {
  return (
    <div>
      {items.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-2 bg-slate-50 relative mb-3">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-600">{t('sch22_item_heading', { index: index + 1 })}</p>
            <Button onClick={() => onRemoveItem(item.id)} variant="danger_outline" size="sm">{t('remove_item_button')}</Button>
          </div>
          <Input label={t('sch22_description_asset_liability')} type="text" id={`s22_2_desc-${item.id}`} value={item.description} onChange={e => onItemChange(index, 'description', e.target.value)} error={(errors as any)?.[index]?.description as string} />
          <Input label={t('sch22_2_original_cost_label')} type="number" id={`s22_2_origCost-${item.id}`} value={item.originalCost} onChange={e => onItemChange(index, 'originalCost', e.target.value)} error={(errors as any)?.[index]?.originalCost as string} />
          <Input label={t('sch22_2_nbv_eop_label')} type="number" id={`s22_2_nbv-${item.id}`} value={item.netBookValueEndOfPeriod} onChange={e => onItemChange(index, 'netBookValueEndOfPeriod', e.target.value)} error={(errors as any)?.[index]?.netBookValueEndOfPeriod as string} />
          <Input label={t('sch22_2_unrealised_gain_label')} type="number" id={`s22_2_gain-${item.id}`} value={item.unrealisedGainRecognised || ''} onChange={e => onItemChange(index, 'unrealisedGainRecognised', e.target.value)} error={(errors as any)?.[index]?.unrealisedGainRecognised as string}/>
          <Input label={t('sch22_2_unrealised_loss_label')} type="number" id={`s22_2_loss-${item.id}`} value={item.unrealisedLossRecognised || ''} onChange={e => onItemChange(index, 'unrealisedLossRecognised', e.target.value)} error={(errors as any)?.[index]?.unrealisedLossRecognised as string}/>
        </div>
      ))}
      <Button onClick={onAddItem} variant="secondary" size="sm">{t('sch22_add_item_button')}</Button>
    </div>
  );
};

// Sub-component for 22.3: Deferred Gains/Losses (if Realisation Basis elected)
const DeferredGainsLossesForm: React.FC<{
  items: OtherScheduleDeferredGainLossItem[];
  onItemChange: (index: number, field: keyof OtherScheduleDeferredGainLossItem, value: any) => void;
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  errors: FormErrorsStep | undefined;
  t: GenericScheduleProps<Schedule22OtherSubScheduleData>['t'];
}> = ({ items, onItemChange, onAddItem, onRemoveItem, errors, t }) => {
  return (
    <div>
      {items.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-2 bg-slate-50 relative mb-3">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-600">{t('sch22_item_heading', { index: index + 1 })}</p>
            <Button onClick={() => onRemoveItem(item.id)} variant="danger_outline" size="sm">{t('remove_item_button')}</Button>
          </div>
          <Input label={t('sch22_description_asset_liability')} type="text" id={`s22_3_desc-${item.id}`} value={item.description} onChange={e => onItemChange(index, 'description', e.target.value)} error={(errors as any)?.[index]?.description as string} />
          <Input label={t('sch22_3_date_realisation_label')} type="date" id={`s22_3_dateReal-${item.id}`} value={item.dateRealisationEvent} onChange={e => onItemChange(index, 'dateRealisationEvent', e.target.value)} error={(errors as any)?.[index]?.dateRealisationEvent as string}/>
          <Input label={t('sch22_3_tax_period_deferred_label')} type="text" id={`s22_3_periodDef-${item.id}`} value={item.taxPeriodDeferred} onChange={e => onItemChange(index, 'taxPeriodDeferred', e.target.value)} error={(errors as any)?.[index]?.taxPeriodDeferred as string}/>
          <RadioGroup label={t('sch22_3_previously_recognised_is_label')} name={`s22_3_prevRecIS-${item.id}`} value={item.previouslyRecognisedInIS || ''} onChange={val => onItemChange(index, 'previouslyRecognisedInIS', val)} options={[{label:t('yes'), value:'yes'}, {label:t('no'), value:'no'}]} t={t} error={(errors as any)?.[index]?.previouslyRecognisedInIS as string}/>
          <Input label={t('sch22_3_gain_realised_label')} type="number" id={`s22_3_gainReal-${item.id}`} value={item.gainRealised || ''} onChange={e => onItemChange(index, 'gainRealised', e.target.value)} error={(errors as any)?.[index]?.gainRealised as string}/>
          <Input label={t('sch22_3_loss_realised_label')} type="number" id={`s22_3_lossReal-${item.id}`} value={item.lossRealised || ''} onChange={e => onItemChange(index, 'lossRealised', e.target.value)} error={(errors as any)?.[index]?.lossRealised as string}/>
        </div>
      ))}
      <Button onClick={onAddItem} variant="secondary" size="sm">{t('sch22_add_item_button')}</Button>
    </div>
  );
};

export const Schedule22_Other: React.FC<GenericScheduleProps<Schedule22OtherSubScheduleData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  const realisationBasisElected = fullFormData.elections.realisationBasis;

  const handleItemListChange = (listName: keyof Schedule22OtherSubScheduleData, index: number, field: string, value: any) => {
    const list = (scheduleData[listName] as any[]).slice();
    list[index] = { ...list[index], [field]: value };
    onChange(listName, list);
  };

  const addItemToList = (listName: keyof Schedule22OtherSubScheduleData, newItemTemplate: any) => {
    const list = (scheduleData[listName] as any[]).slice();
    onChange(listName, [...list, { ...newItemTemplate, id: Date.now().toString() }]);
  };

  const removeItemFromList = (listName: keyof Schedule22OtherSubScheduleData, id: string) => {
    const list = (scheduleData[listName] as any[]).filter(item => item.id !== id);
    onChange(listName, list);
  };

  useEffect(() => {
    let totalIncomeNotInIS = 0;
    scheduleData.incomeLossesNotInIS.forEach(item => {
      totalIncomeNotInIS += safeParseFloat(item.incomeRecognisedNotInIS);
    });

    let totalLossNotInIS = 0;
    scheduleData.incomeLossesNotInIS.forEach(item => {
      totalLossNotInIS += safeParseFloat(item.lossRecognisedNotInIS);
    });
    
    let totalUnrealisedGains = 0;
    scheduleData.unrealisedGainsLosses.forEach(item => {
        totalUnrealisedGains += safeParseFloat(item.unrealisedGainRecognised);
    });

    let totalUnrealisedLosses = 0;
    scheduleData.unrealisedGainsLosses.forEach(item => {
        totalUnrealisedLosses += safeParseFloat(item.unrealisedLossRecognised);
    });

    let totalDeferredGains = 0;
    scheduleData.deferredGainsLosses.forEach(item => {
        totalDeferredGains += safeParseFloat(item.gainRealised);
    });

    let totalDeferredLosses = 0;
    scheduleData.deferredGainsLosses.forEach(item => {
        totalDeferredLosses += safeParseFloat(item.lossRealised);
    });


    const updates: Partial<Schedule22OtherSubScheduleData> = {};
    if (scheduleData.totalIncomeAdjustmentNotInIS !== totalIncomeNotInIS.toFixed(2)) {
        updates.totalIncomeAdjustmentNotInIS = totalIncomeNotInIS.toFixed(2);
    }
    if (scheduleData.totalLossAdjustmentNotInIS !== totalLossNotInIS.toFixed(2)) {
        updates.totalLossAdjustmentNotInIS = totalLossNotInIS.toFixed(2);
    }
    if (scheduleData.totalUnrealisedGainsExcluded !== totalUnrealisedGains.toFixed(2)) {
        updates.totalUnrealisedGainsExcluded = totalUnrealisedGains.toFixed(2);
    }
    if (scheduleData.totalUnrealisedLossesExcluded !== totalUnrealisedLosses.toFixed(2)) {
        updates.totalUnrealisedLossesExcluded = totalUnrealisedLosses.toFixed(2);
    }
    if (scheduleData.totalDeferredGainsRealised !== totalDeferredGains.toFixed(2)) {
        updates.totalDeferredGainsRealised = totalDeferredGains.toFixed(2);
    }
    if (scheduleData.totalDeferredLossesRealised !== totalDeferredLosses.toFixed(2)) {
        updates.totalDeferredLossesRealised = totalDeferredLosses.toFixed(2);
    }

    if (Object.keys(updates).length > 0) {
        for (const [key, value] of Object.entries(updates)) {
            onChange(key as keyof Schedule22OtherSubScheduleData, value);
        }
    }

  }, [
    scheduleData.incomeLossesNotInIS, 
    scheduleData.unrealisedGainsLosses, 
    scheduleData.deferredGainsLosses, 
    onChange,
    scheduleData.totalIncomeAdjustmentNotInIS,
    scheduleData.totalLossAdjustmentNotInIS,
    scheduleData.totalUnrealisedGainsExcluded,
    scheduleData.totalUnrealisedLossesExcluded,
    scheduleData.totalDeferredGainsRealised,
    scheduleData.totalDeferredLossesRealised
  ]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch22_title')}</h3>

      <SectionHeader title={t('sch22_1_title')} />
      <p className="text-xs text-gray-500">{t('sch22_1_intro_note')}</p>
      <IncomeLossesNotInISForm
        items={scheduleData.incomeLossesNotInIS}
        onItemChange={(index, field, value) => handleItemListChange('incomeLossesNotInIS', index, field, value)}
        onAddItem={() => addItemToList('incomeLossesNotInIS', { description: '', isUnrealised: '', id: Date.now().toString() })}
        onRemoveItem={(id) => removeItemFromList('incomeLossesNotInIS', id)}
        errors={(errors?.incomeLossesNotInIS as FormErrorsStep | undefined)}
        t={t}
        realisationBasisElected={realisationBasisElected}
      />
      <Input label={t('sch22_1_total_income_adjustment_label')} type="text" id="s22_1_totalIncomeAdj" value={scheduleData.totalIncomeAdjustmentNotInIS || '0.00'} readOnly className="bg-gray-100 font-semibold" />
      <Input label={t('sch22_1_total_loss_adjustment_label')} type="text" id="s22_1_totalLossAdj" value={scheduleData.totalLossAdjustmentNotInIS || '0.00'} readOnly className="bg-gray-100 font-semibold" />


      {realisationBasisElected && (
        <>
          <SectionHeader title={t('sch22_2_title')} />
          <p className="text-xs text-gray-500">{t('sch22_2_intro_note')}</p>
          <UnrealisedGainsLossesForm
            items={scheduleData.unrealisedGainsLosses}
            onItemChange={(index, field, value) => handleItemListChange('unrealisedGainsLosses', index, field, value)}
            onAddItem={() => addItemToList('unrealisedGainsLosses', { description: '', originalCost: '', netBookValueEndOfPeriod: '', id: Date.now().toString() })}
            onRemoveItem={(id) => removeItemFromList('unrealisedGainsLosses', id)}
            errors={(errors?.unrealisedGainsLosses as FormErrorsStep | undefined)}
            t={t}
          />
          <Input label={t('sch22_2_total_unrealised_gains_label')} type="text" id="s22_2_totalGains" value={scheduleData.totalUnrealisedGainsExcluded || '0.00'} readOnly className="bg-gray-100 font-semibold" />
          <Input label={t('sch22_2_total_unrealised_losses_label')} type="text" id="s22_2_totalLosses" value={scheduleData.totalUnrealisedLossesExcluded || '0.00'} readOnly className="bg-gray-100 font-semibold" />

          <SectionHeader title={t('sch22_3_title')} />
          <p className="text-xs text-gray-500">{t('sch22_3_intro_note')}</p>
          <DeferredGainsLossesForm
            items={scheduleData.deferredGainsLosses}
            onItemChange={(index, field, value) => handleItemListChange('deferredGainsLosses', index, field, value)}
            onAddItem={() => addItemToList('deferredGainsLosses', { description: '', dateRealisationEvent: '', taxPeriodDeferred: '', id: Date.now().toString() })}
            onRemoveItem={(id) => removeItemFromList('deferredGainsLosses', id)}
            errors={(errors?.deferredGainsLosses as FormErrorsStep | undefined)}
            t={t}
          />
          <Input label={t('sch22_3_total_deferred_gains_label')} type="text" id="s22_3_totalDefGains" value={scheduleData.totalDeferredGainsRealised || '0.00'} readOnly className="bg-gray-100 font-semibold" />
          <Input label={t('sch22_3_total_deferred_losses_label')} type="text" id="s22_3_totalDefLosses" value={scheduleData.totalDeferredLossesRealised || '0.00'} readOnly className="bg-gray-100 font-semibold" />
        </>
      )}
    </div>
  );
};
